import { ReadOutlined } from '@ant-design/icons'
import { useDebounce, useRequest } from 'ahooks'
import {
  Button,
  Col,
  Descriptions,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Table,
  TablePaginationConfig,
  Typography,
} from 'antd'
import { Tooltip } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { isNil, omit } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'

import { Editor, MosaicComponent, RiskBar, UserAvatar, UsersSearch, getPointPermission } from '@/components'
import { HotResource } from '@/components/hot-resource/hot-resource.component'
import { CommitteeMemberManager } from '@/components/resource-committee-members'
import { routes } from '@/router/router.config'
import { PAGE_RESOURCE_POINTS_ENUM, PriorityEnum, ResourceType } from '@/schemas'
import { useAxiosWithAtom } from '@/utils'
import { ga, traceEvent } from '@/utils/ga'

import { Header } from '../header'
import { CustomerHomeExport } from './customer-home-export.component'
// import { userInfoAtom } from '../user'
import { customerAdminAtoms, financialOperatorAtoms } from './customer.atom'
import { Customer, CustomerFLow, CustomerFlowStatus, CustomerRole, OverseaRegion } from './customer.schema'
import { FlowStatusButton } from './flow-status/flow-status-button.component'

const { Text } = Typography

export const CustomerHome: React.FC<{}> = () => {
  const { t } = useTranslation()
  React.useEffect(() => {
    ga('event', traceEvent.navigation.events.show_navigation, {
      event_category: traceEvent.navigation.event_categories.ef_pmo,
      event_label: traceEvent.navigation.event_labels.customers,
      action: traceEvent.navigation.action,
    })
  }, [])

  const [selfIds, setSelfIds] = React.useState<string[]>([])
  return (
    <>
      <Header
        leftExtra={
          <div>
            <ReadOutlined />
            <Button
              className='pl-1'
              type='link'
              onClick={() =>
                window.open('https://confluence.agoralab.co/pages/viewpage.action?pageId=1264354583', '_blank')
              }
            >
              {t('customer.document')}
            </Button>
          </div>
        }
        resourceType={ResourceType.CUSTOMER}
        actions={[<CustomerHomeExport key='export' selfIds={selfIds} />]}
      />
      <div className='pt-2'>
        <CommitteeMemberManager resourceType={ResourceType.CUSTOMER} />
      </div>
      <div className='pb-2'>
        <CustomerList withParamPersist={true} onSelfIdsChange={setSelfIds} />
      </div>
    </>
  )
}

export const CustomerList: React.FC<{
  canExport?: boolean
  isFullWidth?: boolean
  withParamPersist?: boolean
  extraOperationLoading?: boolean
  renderExtras?: any
  onSelfIdsChange?: (selfIds: string[]) => void
}> = ({
  withParamPersist = true,
  isFullWidth = true,
  extraOperationLoading = false,
  renderExtras,
  onSelfIdsChange,
}) => {
  const { t } = useTranslation()
  const history = useHistory()

  const defaultPageSize = 50

  const [expandRows, setExpandRows] = React.useState<number[]>([])
  const [formValue, setFormValue] = React.useState<any>({})
  const [tableRowData, setTableRowData] = React.useState<Customer[]>([])
  const [current, setCurrent] = React.useState<number>()
  const [pageSize, setPageSize] = React.useState(defaultPageSize)
  const [total, setTotal] = React.useState(0)
  const [shouldRefresh, setShouldRefresh] = React.useState(false)
  const debouncedNameSearch = useDebounce(formValue.name, { wait: 500 })

  const searchFormRef = React.useRef<{
    form?: FormInstance<any>
    search: (query?: { [key: string]: any }) => void
  }>(null)

  const onFormChange = (formValue: any) => {
    formValue = { ...formValue, flowStatus: formValue.flowStatus }
    persistSearch({
      page: formValue.page,
      pageSize: formValue.pageSize,
      formValue: omit(formValue, ['page', 'pageSize']),
    })
  }

  const translateMap = {
    [CustomerFlowStatus.待研发审批]: t(`customer.flowStatus.${CustomerFlowStatus.待研发审批}.title`),
    [CustomerFlowStatus.研发未开始]: t(`customer.flowStatus.${CustomerFlowStatus.研发未开始}.title`),
    [CustomerFlowStatus.项目已驳回]: t(`customer.flowStatus.${CustomerFlowStatus.项目已驳回}.title`),
    [CustomerFlowStatus.研发进行中]: t(`customer.flowStatus.${CustomerFlowStatus.研发进行中}.title`),
    [CustomerFlowStatus.研发暂停中]: t(`customer.flowStatus.${CustomerFlowStatus.研发暂停中}.title`),
    [CustomerFlowStatus.项目已取消]: t(`customer.flowStatus.${CustomerFlowStatus.项目已取消}.title`),
    [CustomerFlowStatus.待客户验收]: t(`customer.flowStatus.${CustomerFlowStatus.待客户验收}.title`),
    [CustomerFlowStatus.结果追踪中]: t(`customer.flowStatus.${CustomerFlowStatus.结果追踪中}.title`),
    [CustomerFlowStatus.结果复核中]: t(`customer.flowStatus.${CustomerFlowStatus.结果复核中}.title`),
    [CustomerFlowStatus.待项目结项]: t(`customer.flowStatus.${CustomerFlowStatus.待项目结项}.title`),
    [CustomerFlowStatus.项目已结项]: t(`customer.flowStatus.${CustomerFlowStatus.项目已结项}.title`),
    [CustomerFlowStatus.项目已失败]: t(`customer.flowStatus.${CustomerFlowStatus.项目已失败}.title`),
  }

  const { run: runQueryList, loading } = useRequest(
    ({ params }) => {
      return {
        url: '/api/customer/list',
        method: 'get',
        params,
      }
    },
    {
      manual: true,
    }
  )

  const { data: admins } = useAxiosWithAtom(customerAdminAtoms, {
    url: '/api/customer/admins',
  })

  const { data: financialOperators } = useAxiosWithAtom(financialOperatorAtoms, {
    url: '/api/customer/financial-operators',
  })

  const queryList = (params = {}) => {
    if (!Object.keys(params).length) return
    runQueryList({ params }).then(({ items, pagination: { total } }) => {
      setTotal(total)
      const remoteData = items.map((item: Customer) => {
        return {
          ...item,
          key: item.selfId,
        }
      })
      onSelfIdsChange?.(remoteData.map((item: Customer) => item.selfId))
      setTableRowData(remoteData)
    })
  }

  React.useEffect(() => {
    if (debouncedNameSearch === formValue.name) {
      queryList(formValue)
    }
  }, [formValue, debouncedNameSearch])

  React.useEffect(() => {
    if (shouldRefresh) {
      queryList(formValue)
      setShouldRefresh(false)
    }
  }, [shouldRefresh])

  const persistSearch = (
    params?: Partial<{
      page: number
      pageSize: number
      formValue: { orderKey?: string; order?: string }
    }>
  ) => {
    if (!withParamPersist) {
      setFormValue(params?.formValue)
      return
    }
    const { page: paramPage, pageSize: paramPageSize, formValue: paramFormValue } = params || {}
    const { page: _page, pageSize: _pageSize, ...value } = queryString.parse(location.search) as any
    const page = !isNil(paramPage) ? paramPage : !isNil(_page) ? _page : 0
    const pageSize = paramPageSize ?? _pageSize ?? defaultPageSize
    const formValue = { ...value, ...paramFormValue }

    const search = { ...formValue, page, pageSize } as any
    search.flowStatus = search.flowStatus ?? []
    history.replace({
      search: queryString.stringify(Object.assign(queryString.parse(location.search), search)),
    })
    setCurrent(Number(search.page) + 1)
    setPageSize(Number(search.pageSize))
    setFormValue(search)

    return search
  }

  const handleTableChange = ({ current, pageSize }: TablePaginationConfig, _filters: any, sorter: any) => {
    if (pageSize) {
      setPageSize(pageSize)
    }
    if (current) {
      setCurrent(current)
    }
    const page = current ? current - 1 : 0
    const size = pageSize ? pageSize : defaultPageSize
    persistSearch({
      pageSize: size,
      page,
      formValue: { orderKey: sorter?.columnKey, order: sorter?.order },
    })
  }

  const expandDetail = (record: Customer & { key: number }) => {
    const key = record.key
    const index = expandRows.indexOf(key)
    if (~index) {
      setExpandRows(expandRows.filter((_, i) => i !== index))
    } else {
      setExpandRows([...expandRows, record.key])
    }
  }

  const getColumnSearchProps = (dataIndex: string): ColumnType<Customer> => ({
    render: (record: any) => {
      if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, record.permissions).canRead) {
        return <MosaicComponent />
      }
      const email = record.roles.find((r: any) => r.role === dataIndex)?.email
      return (
        <Text className='flex-grow flex'>
          <UserAvatar showName email={email} />
        </Text>
      )
    },
  })

  const columns = [
    {
      width: 150,
      title: t('customer.负责人'),
      dataIndex: 'email',
      key: 'email',
      fixed: 'left' as const,
      render: (email: string, record: Customer) => {
        return (
          <Text className='flex-grow flex'>
            <RiskBar className='mr-20px' risk={record.risk} />
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_负责人, record.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <UserAvatar showName email={email} />
            )}
          </Text>
        )
      },
    },
    {
      width: 400,
      ellipsis: true,
      title: t('customer.name'),
      fixed: 'left' as const,
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Customer) => {
        return (
          <Link
            className='leading-[30px]'
            onClick={() => {
              window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_customers)
            }}
            to={routes.customer.detail(record.selfId)}
          >
            <HotResource selfId={record.selfId} type={record.type} readonly hotData={record.hots} />
            <span className='ml-4'>
              {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, record.permissions).canRead ? (
                name
              ) : (
                <MosaicComponent content={name} />
              )}
            </span>
          </Link>
        )
      },
    },
    {
      width: 150,
      title: t('customer.服务Owner'),
      sorter: true,
      key: CustomerRole.服务Owner, // 字段被后端 order 所使用
      ...getColumnSearchProps(CustomerRole.服务Owner),
    },
    {
      width: 100,
      title: t('customer.region'),
      dataIndex: 'region',
      key: 'region',
      sorter: true,
      render: (region: string, record: Customer) => {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_所属区域, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          region
        )
      },
    },
    {
      width: 120,
      title: t('customer.TO'),
      key: CustomerRole.TO, // 字段被后端 order 所使用
      sorter: true,
      ...getColumnSearchProps(CustomerRole.TO),
    },
    {
      width: 100,
      title: t('customer.detail.priority'),
      key: 'priority',
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          t(`form.priority.${PriorityEnum[record.priority]}`)
        )
      },
    },
    {
      width: 120,
      title: t('customer.status.title'),
      key: 'flowStatus',
      sorter: true,
      render(record: any) {
        if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, record.permissions).canRead) {
          return <MosaicComponent />
        }

        const status = translateMap[record.flowStatus as keyof typeof translateMap]
        if (record.flowStatus === CustomerFlowStatus.研发进行中 && record.expectTime) {
          const isExceed = moment().isAfter(moment(record.expectTime), 'date')
          const time = moment(record.expectTime).utc().format('YYYY-MM-DD')
          return (
            <Tooltip visible={isExceed ? undefined : false} title={t('customer.home.status.onGoing.warning', { time })}>
              <span className={`${isExceed ? 'text-yellow-500' : 'text-green-500'} cursor-pointer`}>{status}</span>
            </Tooltip>
          )
        }

        if (
          [CustomerFlowStatus.结果复核中, CustomerFlowStatus.结果追踪中].includes(record.flowStatus) &&
          record.endDate
        ) {
          const isExceed = moment().isAfter(moment(record.endDate), 'date')
          const time = moment(record.endDate).utc().format('YYYY-MM-DD')
          return (
            <Tooltip
              visible={isExceed ? undefined : false}
              title={t('customer.home.status.onResult.warning', { time })}
            >
              <span className={`${isExceed ? 'text-yellow-500' : 'text-green-500'} cursor-pointer`}>{status}</span>
            </Tooltip>
          )
        }

        if (record.flowStatus === CustomerFlowStatus.待客户验收) {
          const isExceed = moment().isSameOrAfter(moment(record.expectValidationTime).utc(), 'date')
          const time = moment(record.expectValidationTime).utc().format('YYYY-MM-DD')
          return (
            <Tooltip
              title={
                record.expectValidationTime
                  ? isExceed
                    ? t('customer.flowStatus.CustomerWaitForValidate.expectValidationTime.exceed', { time })
                    : t('customer.flowStatus.CustomerWaitForValidate.expectValidationTime.nonExceed', { time })
                  : t('customer.flowStatus.CustomerWaitForValidate.expectValidationTime.info')
              }
            >
              <span
                className={`${
                  record.expectValidationTime ? (isExceed ? 'text-yellow-500' : 'text-green-500') : ''
                } cursor-pointer`}
              >
                {status}
              </span>
            </Tooltip>
          )
        }
        return status
      },
    },
    {
      width: 120,
      title: t('customer.actualWorkload.title'),
      key: 'actual_workload', // 字段被后端 order 所使用
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          Number(record.actualWorkload).toFixed(0)
        )
      },
    },
    {
      width: 120,
      title: t('customer.projectManager.title'),
      key: CustomerRole.项目经理, // 字段被后端 order 所使用
      sorter: true,
      ...getColumnSearchProps(CustomerRole.项目经理),
    },
  ]

  if (isFullWidth) {
    columns.push({
      width: 200,
      title: t('customer.operation.title'),
      fixed: 'right' as const,
      dataIndex: '',
      key: 'x',
      render: (customer: Customer & { key: number }) => {
        const isOversea = customer.region === OverseaRegion
        const customerAdmins = isOversea ? admins.oversea : admins.china
        const customerFinancialOperators = isOversea ? financialOperators.oversea : financialOperators.china

        return (
          <>
            {~expandRows.indexOf(customer.key) ? (
              <Button onClick={() => expandDetail(customer)} type='link' size='small'>
                {t('customer.operation.close')}
              </Button>
            ) : (
              <Button onClick={() => expandDetail(customer)} type='link' size='small'>
                {t('customer.operation.open')}
              </Button>
            )}
            <FlowStatusButton
              financialOperators={customerFinancialOperators}
              admins={customerAdmins}
              refresh={() => setShouldRefresh(true)}
              customer={customer}
            />
          </>
        )
      },
    } as any)
  }

  if (renderExtras) {
    columns.push({
      align: 'center',
      width: 80,
      render: renderExtras,
    } as any)
  }

  const CustomerExtendDetail: React.FC<{ customer: Customer }> = ({ customer }) => {
    const flows = customer.flows || []
    const getReviewMessage = (flows: CustomerFLow[]) => {
      if (flows.find((f) => f.status === CustomerFlowStatus.研发未开始)) {
        const flow = flows.find((f) => f.status === CustomerFlowStatus.研发未开始)
        return {
          flow,
          text: flow?.additions.find((a) => a.type === 'reviewComment')?.value || '-',
        }
      }
      if (flows.find((f) => f.status === CustomerFlowStatus.项目已驳回)) {
        const flow = flows.find((f) => f.status === CustomerFlowStatus.项目已驳回)
        return {
          flow,
          text: flow?.additions.find((a) => a.type === 'reviewComment')?.value || '-',
        }
      }
      return {
        flow: flows.find((f) => f.status === CustomerFlowStatus.待研发审批),
        text: t('customer.review.history.waiting'),
      }
    }
    // const lastReview = customer.reviews
    //   ?.filter((review) => [ReviewFormActions.通过, ReviewFormActions.驳回].includes(review.action))
    //   .sort((a, b) => b.id - a.id)[0]
    const { text, flow } = getReviewMessage(flows)

    return (
      <Descriptions bordered style={{ background: 'white' }} column={6}>
        <Descriptions.Item label={t('customer.detail.dependencies')} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_依赖项, customer.permissions).canRead}
            content={customer.dependencies}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('customer.detail.reviewTime')} span={6}>
          {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, customer.permissions).canRead ? (
            <MosaicComponent />
          ) : flow ? (
            moment(flow?.createdAt).utc().format('YYYY-MM-DD')
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('customer.detail.reviewComment')} span={6}>
          <span>
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, customer.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              text
            )}
          </span>
        </Descriptions.Item>
        <Descriptions.Item label={t('customer.detail.background')} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_背景, customer.permissions).canRead}
            content={customer.background}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('customer.detail.goal')} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_交付目标, customer.permissions).canRead}
            content={customer.goal}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('customer.detail.standard')} span={6}>
          <Editor
            editable={false}
            canRead={
              getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], customer.permissions).canRead
            }
            content={customer.standard}
          />
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              {t('customer.detail.devReports')}
              <span className='font-bold ml-2 text-red-500'>
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.周报_内容, customer.permissions).canRead ? (
                  customer.thisWeekReport ? (
                    <span>
                      ( {moment(customer.thisWeekReport.startDate).format('YYYY-MM-DD')} -{' '}
                      {moment(customer.thisWeekReport.endDate).format('YYYY-MM-DD')} )
                    </span>
                  ) : null
                ) : (
                  <MosaicComponent />
                )}
              </span>
            </span>
          }
          labelStyle={{ width: '22.22%' }}
          span={6}
        >
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.周报_内容, customer.permissions).canRead}
            content={customer.thisWeekReport?.extras?.find((e: any) => e.field === '当前进展')?.content}
          />
        </Descriptions.Item>
      </Descriptions>
    )
  }

  return (
    <div>
      <CustomerSearchForm ref={searchFormRef} onChange={onFormChange} />
      <Table
        className='overflow-x-hidden'
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        pagination={{
          showSizeChanger: true,
          total,
          pageSize,
          current,
          showTotal: (total) => t(`customer.count`, { count: total }),
        }}
        columns={columns}
        loading={loading || extraOperationLoading}
        dataSource={tableRowData}
        expandable={{
          expandedRowKeys: expandRows,
          expandRowByClick: true,
          expandedRowRender: (customer) => <CustomerExtendDetail customer={customer} />,
          expandIcon: () => null,
          columnWidth: 0,
        }}
      />
    </div>
  )
}

const searchType = [
  {
    label: 'all',
    value: 'all',
  },
  ...Object.entries(CustomerRole).map(([key, v]) => ({
    label: key,
    value: v,
  })),
]

const CustomerSearchForm = React.forwardRef<
  any,
  {
    onChange: (value: any) => void
  }
>(({ onChange, ...props }, ref) => {
  const { t } = useTranslation()

  const translateMap = {
    [CustomerFlowStatus.待研发审批]: t(`customer.flowStatus.${CustomerFlowStatus.待研发审批}.title`),
    [CustomerFlowStatus.研发未开始]: t(`customer.flowStatus.${CustomerFlowStatus.研发未开始}.title`),
    [CustomerFlowStatus.项目已驳回]: t(`customer.flowStatus.${CustomerFlowStatus.项目已驳回}.title`),
    [CustomerFlowStatus.研发进行中]: t(`customer.flowStatus.${CustomerFlowStatus.研发进行中}.title`),
    [CustomerFlowStatus.研发暂停中]: t(`customer.flowStatus.${CustomerFlowStatus.研发暂停中}.title`),
    [CustomerFlowStatus.项目已取消]: t(`customer.flowStatus.${CustomerFlowStatus.项目已取消}.title`),
    [CustomerFlowStatus.待客户验收]: t(`customer.flowStatus.${CustomerFlowStatus.待客户验收}.title`),
    [CustomerFlowStatus.结果追踪中]: t(`customer.flowStatus.${CustomerFlowStatus.结果追踪中}.title`),
    [CustomerFlowStatus.结果复核中]: t(`customer.flowStatus.${CustomerFlowStatus.结果复核中}.title`),
    [CustomerFlowStatus.待项目结项]: t(`customer.flowStatus.${CustomerFlowStatus.待项目结项}.title`),
    [CustomerFlowStatus.项目已结项]: t(`customer.flowStatus.${CustomerFlowStatus.项目已结项}.title`),
    [CustomerFlowStatus.项目已失败]: t(`customer.flowStatus.${CustomerFlowStatus.项目已失败}.title`),
  }

  const [form] = Form.useForm<any>()

  const { data, loading } = useRequest(
    () => {
      return {
        url: '/api/customer/regions',
        method: 'get',
      }
    },
    {
      refreshDeps: [],
    }
  )

  const regions = data || []

  const search = (query: any = {}) => {
    const { page, pageSize } = queryString.parse(location.search)
    const other = form.getFieldsValue()
    const values = {
      page,
      pageSize,
      ...other,
      ...query,
    }
    return onChange(values)
  }

  React.useImperativeHandle(ref, () => ({
    search,
    form,
  }))

  const defaultFilterStatus = [
    CustomerFlowStatus.待研发审批,
    CustomerFlowStatus.研发未开始,
    CustomerFlowStatus.研发进行中,
    CustomerFlowStatus.研发暂停中,
    CustomerFlowStatus.待客户验收,
    CustomerFlowStatus.结果追踪中,
    CustomerFlowStatus.结果复核中,
    CustomerFlowStatus.待项目结项,
  ]

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, pageSize, ...value } = queryString.parse(location.search)
    let flowStatus = defaultFilterStatus
    if (!isNil(page)) {
      flowStatus = value.flowStatus as CustomerFlowStatus[]
    }
    const formData = {
      email: value.email || undefined,
      name: value.name || undefined,
      searchType: value.searchType || 'all',
      region: value.region || undefined,
      flowStatus,
    }

    form.setFieldsValue(formData)

    const other = formData
    const values = {
      page,
      pageSize,
      ...other,
    }
    onChange(values)
  }, [])

  return (
    <div className='w-full py-4'>
      <Form {...props} form={form} name='customerSearch' layout='inline' onFieldsChange={() => search({ page: 0 })}>
        <Row className='w-full' gutter={[{ xs: 4, sm: 8, md: 16, lg: 24 }, 16]}>
          <Col span={8}>
            <Input.Group compact className='!flex'>
              <Form.Item name='email' className='!flex-1'>
                <UsersSearch placeholder={t('customer.负责人')} />
              </Form.Item>
              <Form.Item name='searchType' className='w-100px'>
                <Select
                  options={searchType.map((s) => {
                    return {
                      label: t(s.label),
                      value: s.value,
                    }
                  })}
                />
              </Form.Item>
            </Input.Group>
          </Col>
          <Col span={8}>
            <Form.Item name='name'>
              <Input placeholder={t('customer.name')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item required name='region'>
              <Select
                allowClear
                loading={loading}
                placeholder={t('customer.region')}
                options={regions.concat(['空']).map((r: any) => ({
                  label: r,
                  value: r,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8} xxl={10} xl={16}>
            <Form.Item name='flowStatus'>
              <Select
                mode='multiple'
                placeholder={t('status')}
                allowClear
                options={Object.keys(translateMap).map((c) => ({
                  value: c,
                  label: translateMap[c as keyof typeof translateMap],
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  )
})
